import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import {
  type FontScaleId,
  FONT_SCALE_IDS,
  applyFontScaleToDocument,
  readStoredFontScale,
  writeStoredFontScale,
} from "@/lib/fontScale";
import { normalizeBackendUrl } from "@/lib/api";

export type { FontScaleId };

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
const TOKEN_KEY = "token";

interface FontScaleContextValue {
  fontScale: FontScaleId;
  setFontScale: (id: FontScaleId, options?: { persistRemote?: boolean }) => void;
}

const FontScaleContext = createContext<FontScaleContextValue>({
  fontScale: "md",
  setFontScale: () => {},
});

export function FontScaleProvider({ children }: { children: React.ReactNode }) {
  const [fontScale, setFontScaleState] = useState<FontScaleId>(() => readStoredFontScale());

  useLayoutEffect(() => {
    applyFontScaleToDocument(fontScale);
    writeStoredFontScale(fontScale);
  }, [fontScale]);

  const setFontScale = useCallback((id: FontScaleId, options?: { persistRemote?: boolean }) => {
    if (!(FONT_SCALE_IDS as readonly string[]).includes(id)) return;
    setFontScaleState(id);
    if (options?.persistRemote === false) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uiFontScale: id }),
    }).catch(() => {});
  }, []);

  const value = useMemo(() => ({ fontScale, setFontScale }), [fontScale, setFontScale]);

  return (
    <FontScaleContext.Provider value={value}>
      {children}
    </FontScaleContext.Provider>
  );
}

export function useFontScale() {
  return useContext(FontScaleContext);
}
